import { docToScenePayload, type AdminApi } from '@roomtreatment/api-client';
import type {
  EditorDoc,
  ModalPipeline,
  FreqResponseResponse,
  RT60Response,
  SoundstageResponse,
} from '@roomtreatment/core';

export interface AnalysisResult {
  freq: FreqResponseResponse;
  rt60: RT60Response;
  soundstage: SoundstageResponse | null;
}

export interface AnalysisRunnerOptions {
  api: AdminApi;
  debounceMs?: number;
  onResult: (result: AnalysisResult, token: number) => void;
  onError?: (error: unknown, token: number) => void;
  onLoading?: (loading: boolean) => void;
}

export interface AnalysisRunner {
  /** Schedule analysis for this doc; bumps internal token. Returns token. */
  schedule(doc: EditorDoc, pipeline?: ModalPipeline): number;
  /** Force immediate run (clears debounce). */
  computeNow(doc: EditorDoc, pipeline?: ModalPipeline): number;
  cancel(): void;
}

export function createAnalysisRunner(opts: AnalysisRunnerOptions): AnalysisRunner {
  const debounceMs = opts.debounceMs ?? 800;
  let token = 0;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let cancelled = false;

  function clearDebounce(): void {
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
  }

  async function run(doc: EditorDoc, pipeline: ModalPipeline | undefined, runToken: number): Promise<void> {
    opts.onLoading?.(true);
    try {
      const payload = docToScenePayload(doc);
      const ssPayload = {
        ...payload,
        listener_yaw_rad: doc.listener?.rotationY ?? 0,
      };
      const [freq, rt60, soundstage] = await Promise.all([
        opts.api.freqResponse(payload, pipeline),
        opts.api.rt60({ ...payload, furnishing: doc.furnishing }, pipeline),
        opts.api.soundstage(ssPayload).catch(() => null),
      ]);
      if (cancelled || runToken !== token) return;
      opts.onResult({ freq, rt60, soundstage }, runToken);
    } catch (err) {
      if (cancelled || runToken !== token) return;
      opts.onError?.(err, runToken);
    } finally {
      if (runToken === token) opts.onLoading?.(false);
    }
  }

  return {
    schedule(doc, pipeline) {
      cancelled = false;
      token += 1;
      const runToken = token;
      clearDebounce();
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        void run(doc, pipeline, runToken);
      }, debounceMs);
      return runToken;
    },

    computeNow(doc, pipeline) {
      cancelled = false;
      token += 1;
      const runToken = token;
      clearDebounce();
      void run(doc, pipeline, runToken);
      return runToken;
    },

    cancel() {
      cancelled = true;
      clearDebounce();
    },
  };
}
